'use client';

import { useUserData } from '@/app/_shared/context/auth/user-data-context';
import { ContentType } from '@/graphql/graphql';
import { useTranslations } from 'next-intl';
import { CategoriesDropDown } from '../catagories-dropdown';
import { CatalogDropDown } from '../catalog-dropdown';
import { GuestDropDown } from '../guest-dropdown';
import { LicensesDropDown } from '../licenses-dropdown';
import NavigationLink from '../navigation-link';
import { useController } from './controller';
import { NavigationProps } from './interfaces';

export const Navigation = ({ onLinkClickAction, isMenuOpen }: NavigationProps) => {
    const t = useTranslations('Navigation');
    const th = useTranslations('Header');
    const { isBuyer, isSeller, loading } = useUserData();

    const { categoriesProgram, categoriesFormat, isLinkActive } = useController({
        translateNavigation: t as (key: string) => string,
    });
    const isMobileMenu = Boolean(onLinkClickAction);
    // Only re-expand the active section each time the mobile menu panel is actually opened —
    // not just whenever the current page matches it, otherwise closing and reopening the panel
    // (which itself counts as an outside click while the panel was visible) would leave the
    // dropdown collapsed since its own "should auto-open" signal never toggled back to true.
    const autoExpandActiveSection = isMobileMenu && Boolean(isMenuOpen);

    if (loading) {
        return (
            <nav className="text-content-on-dark transition-all duration-300 flex flex-col max-lg:gap-2 lg:flex-row lg:items-center lg:space-x-6" />
        );
    }

    const showGuestMenu = !isSeller && !isBuyer;
    return (
        <nav className="text-content-on-dark transition-all duration-300 flex flex-col max-lg:gap-2 lg:flex-row lg:items-center lg:space-x-6">
            {showGuestMenu ? (
                <GuestDropDown
                    isActive={isLinkActive('/showcase-buyers') || isLinkActive('/showcase-sellers')}
                    onLinkClickAction={onLinkClickAction}
                />
            ) : null}
            {isBuyer || showGuestMenu ? (
                <>
                    <CategoriesDropDown
                        isActive={isLinkActive('/content-programs')}
                        contentType={ContentType.Program}
                        categories={categoriesProgram}
                        onLinkClickAction={onLinkClickAction}
                        autoExpand={autoExpandActiveSection}
                    />
                    <CategoriesDropDown
                        isActive={isLinkActive('/content-formats')}
                        contentType={ContentType.Format}
                        categories={categoriesFormat}
                        onLinkClickAction={onLinkClickAction}
                        autoExpand={autoExpandActiveSection}
                    />
                </>
            ) : null}
            {isSeller ? (
                <>
                    <NavigationLink href="/dashboard" isActive={isLinkActive('/dashboard')} onClick={onLinkClickAction}>
                        {t('seller.dashboard')}
                    </NavigationLink>
                    <CatalogDropDown
                        callback={() => !!onLinkClickAction && onLinkClickAction()}
                        autoExpand={autoExpandActiveSection}
                    />
                </>
            ) : null}
            {isSeller || isBuyer ? (
                <LicensesDropDown
                    callback={() => !!onLinkClickAction && onLinkClickAction()}
                    autoExpand={autoExpandActiveSection}
                />
            ) : null}
            {isBuyer ? (
                <div className="lg:hidden">
                    <NavigationLink
                        href="/transactions"
                        isActive={isLinkActive('/transactions')}
                        onClick={onLinkClickAction}
                    >
                        {t('buyer.transactions')}
                    </NavigationLink>
                </div>
            ) : null}
            {isBuyer || isSeller ? (
                <div className="lg:hidden">
                    <NavigationLink
                        href="/account"
                        isActive={isLinkActive('/account', 'inclusive')}
                        onClick={onLinkClickAction}
                    >
                        {th('accountSettings')}
                    </NavigationLink>
                </div>
            ) : null}
            {isBuyer ? (
                <div className="lg:hidden">
                    <NavigationLink
                        href="/settings"
                        isActive={isLinkActive('/settings', 'inclusive')}
                        onClick={onLinkClickAction}
                    >
                        {th('technicalSettings')}
                    </NavigationLink>
                </div>
            ) : null}
        </nav>
    );
};
